// "Tipologia di connessione e angolo": θ (slider + number, 1–180) and the connection type tiles, with the panel /
// three-member notes.
import { memo, type FC } from "react";
import { FormField } from "../../../components/ds2/FormField";
import { Numeric } from "../../../components/ds2/Numeric";
import { useReadOnly } from "../../../components/ui/ReadOnlyGate";
import useI18n from "../../../hooks/useTranslations";
import { useMissing } from "./missingFields";
import Note from "./Note";
import type { ConnectionType } from "../../../http/screwConnector/formulas";
import { THETA_DEFAULT } from "../../../http/screwConnector/useAutoSaveScrewConnectorCalculation";
import { numOrNull, type EngineView } from "./uiState";
import { useNumberDraft } from "./useNumberDraft";
import { useScForm } from "./useScForm";
import ConnectionTypeTiles from "./ConnectionTypeTiles";
import styles from "./ConnectionTypeCard.module.css";

type ConnectionTypeCardProps = {
  view: EngineView;
  // the page re-constrains the materials of the new type (changeConnectionType)
  onConnectionTypeChange?: (type: ConnectionType) => void;
};

const ConnectionTypeCard: FC<ConnectionTypeCardProps> = ({ view, onConnectionTypeChange }) => {
  const disabled = useReadOnly();
  const calc = useI18n().translation.screwConnectorModule.calculation;
  const t = calc.typeSection;
  const { values, set } = useScForm();
  const draft = useNumberDraft();
  const isMissing = useMissing();
  const { ui } = view;
  const theta = view.values ? numOrNull(view.values.thetaNum) : values.theta;

  return (
    <>
      <FormField label={t.theta} footer={{ type: "info", message: t.thetaUnit }} disabled={disabled}>
        {({ labelId, disabled }) => (
          <div className={styles.thetaRow}>
            <input
              type="range"
              min={1}
              max={180}
              step={1}
              aria-label={t.theta}
              // at the default while θ is emptied
              value={theta === null ? THETA_DEFAULT : Math.min(180, Math.max(1, theta))}
              disabled={disabled}
              onChange={(e) => set("theta", Number(e.currentTarget.value))}
            />
            <Numeric
              id={labelId}
              name="thetaNum"
              width="80px"
              decimalScale={0}
              disabled={disabled}
              isInvalid={isMissing("thetaNum", theta === null)}
              {...draft("theta", theta, (v) => set("theta", v))}
            />
          </div>
        )}
      </FormField>
      <FormField label={t.connectionType} disabled={disabled}>
        <ConnectionTypeTiles
          label={t.connectionType}
          value={view.values?.materiale1 ?? values.connectionType}
          onChange={(type) => (onConnectionTypeChange ? onConnectionTypeChange(type) : set("connectionType", type))}
          disabled={disabled}
        />
        {ui.visible.panelNote ? <Note html={calc.notes.panel} /> : null}
        {ui.visible.threeNote && ui.texts.threeNote ? <Note html={ui.texts.threeNote} /> : null}
      </FormField>
    </>
  );
};

// Memoized: it re-renders through its own props (the engine view among them) and contexts, not whenever its parent
// does.
export default memo(ConnectionTypeCard);
